import './PreviewInvoice.less';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Stripe from 'stripe';
import { Button, InputNumber, Checkbox, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../../app/store';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { fetchCreditBalance } from '../../app/ActionTypes/creditBalanceActionTypes';
import {
  BASE_PRODUCTS,
  SimpleCommonInvoiceSubscriptionItem,
  PriceWithRecurring,
  formatCurrency,
  caculatelInvoicePreviewToday,
  getPendingQuantity,
  calculateInvoicePreviewForNextTime,
  upsertSubscription,
  getRealQuantity,
  _getMatchingAddonPrice,
  getCreditsMonthly,
  isBasePlanProduct,
  isAddonProduct,
} from './helpers';
import { Notify } from '../../services/notificationService';
import { sleep } from '../../utils/sleep';
import { openSubscriptionPortal } from '../../services/subscription';
import { Product } from '../../interfaces/product';
import { formatDate } from '../../utils/timeUtils';
import { AxiosError } from 'axios';

/**
TODO:
- [x] handle case: customer makes no subscription changes
- [x] handle case: customer has no subscription
- [x] handle case: customer updates an active subscription
- [x] handle case: customer a subscription but it's a trial
- [ ] handle case: customer a subscription but it's paused or past due
- [x] handle case: customer decrease credits -> they should be able to use the credits until the end of the current period
- [x] handle case: customer is subscribing to yearly Pro plan
- [x] handle case: customer has no payment method
- [ ] handle case: customer changes recurring interval
- [ ] handle case: the real amount is different than the preview amount
- [ ] handle errors
- [x] close buy-credits page after success 
- [ ] add link to read preview invoice
 */

const ErrorConfigInvalid: React.FC = () => (
  <div>
    Credit upgrades are temporarily unavailable due to a configuration issue.
    Please contact <Link to="mailto:support@cirra.ai">support@cirra.ai</Link>{' '}
    for assistance.
  </div>
);

const ErrorPaymentMethodMissing: React.FC = () => {
  return (
    <div>
      You have not added a payment method yet. Click
      <Button
        type="link"
        style={{
          display: 'inline',
          padding: 0,
          margin: '0 0.25rem',
        }}
        onClick={() => openSubscriptionPortal()}
      >
        here
      </Button>
      to add a payment method.
    </div>
  );
};

const PreviewInvoice: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { subscriptionInfo } = useSelector(
    (state: RootState) => state.subscription
  );
  const customerId = subscriptionInfo?.stripeCustomerId;
  const products = useSelector((state: RootState) => state.products.products);

  const { remainingCredits, oneTimeCreditsRemaining, oneTimeCreditsPurchased } =
    useCreditBalance();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlanPriceId, setSelectedPlanPriceId] = useState<
    string | undefined
  >(undefined);
  const [planProduct, setPlanProduct] = useState<Product | null>(null);
  const [planPrices, setPlanPrices] = useState<PriceWithRecurring[]>([]);
  const [addonProduct, setAddonProduct] = useState<Product | null>(null);
  const [addonPrices, setAddonPrices] = useState<PriceWithRecurring[]>([]);
  const [currentSubscription, setCurrentSubscription] =
    useState<Stripe.Subscription | null>(null);
  const [formerAddonRealQuantity, setFormerAddonRealQuantity] =
    useState<number>(0);
  const [formerAddonPendingQuantity, setFormerAddonPendingQuantity] =
    useState<number>(0);
  const [newAddonQuantity, setNewAddonQuantity] = useState<number>(0);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState<boolean>(false);
  const [finalInvoiceToday, setFinalInvoiceToday] =
    useState<Stripe.Invoice | null>(null);
  const [finalInvoiceNextTime, setFinalInvoiceNextTime] =
    useState<Stripe.Invoice | null>(null);
  const [requestedInvoiceLines, setRequestedInvoiceLines] = useState<
    SimpleCommonInvoiceSubscriptionItem[]
  >([]);
  const [isConfirmedConsent, setIsConfirmedConsent] = useState<boolean>(false);
  const [errorElement, setErrorElement] = useState<React.ReactNode | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const hasChangedPlan = useMemo(() => {
    if (!finalInvoiceToday) {
      return false;
    }
    const lineForNewlyRegisteredPlan = finalInvoiceToday.lines.data.find(
      (line) => {
        const lineProductId = line.pricing?.price_details?.product || '';
        return (
          line.parent?.subscription_item_details?.subscription !==
            currentSubscription?.id &&
          (BASE_PRODUCTS.includes(lineProductId) ||
            lineProductId === planProduct?.id)
        );
      }
    );
    return !!lineForNewlyRegisteredPlan;
  }, [finalInvoiceToday, currentSubscription?.id, planProduct?.id]);

  const hasChangedCredits = newAddonQuantity !== formerAddonPendingQuantity;
  const increasingCredits = newAddonQuantity > formerAddonPendingQuantity;
  const decreasingCredits = newAddonQuantity < formerAddonPendingQuantity;

  // Format renewal date and calculate monthly charge
  const formatRenewalInfo = () => {
    if (!currentSubscription || !finalInvoiceNextTime) return null;

    // Get the next renewal date
    let renewalDate: Date;

    // For upgrades, the subscription period will reset so next renewal is one month/year from today
    // This includes trial conversions
    const isUpgrade = newAddonQuantity > formerAddonPendingQuantity;

    if (isUpgrade) {
      // Upgrade takes effect immedicately so next renewal is one month/year from today
      const period =
        currentSubscription?.items?.data?.[0]?.plan?.interval || 'month';
      renewalDate = new Date();
      if (period === 'year') {
        renewalDate.setFullYear(renewalDate.getFullYear() + 1);
      } else {
        renewalDate.setMonth(renewalDate.getMonth() + 1);
      }
    } else if (currentSubscription?.items?.data?.[0]?.current_period_end) {
      // For existing subscriptions, use the current_period_end which is when the next billing cycle starts
      renewalDate = new Date(
        currentSubscription.items.data[0].current_period_end * 1000
      );
    } else if (finalInvoiceNextTime?.period_end) {
      // For new subscriptions, use the invoice's period_end
      renewalDate = new Date(finalInvoiceNextTime.period_end * 1000);
    } else {
      // Last resort: calculate 30 days from now for new subscriptions
      renewalDate = new Date();
      renewalDate.setDate(renewalDate.getDate() + 30);
    }

    const monthlyCharge = finalInvoiceNextTime.total / 100; // Convert from cents
    return {
      date: formatDate(renewalDate),
      amount: `$${monthlyCharge.toFixed(2)}/month`,
    };
  };

  const renewalInfo = formatRenewalInfo();

  const subscriptionDetails = subscriptionInfo?.subscription;

  // Load Stripe data: products, prices, subscription.
  useEffect(() => {
    if (!customerId || !products.length) {
      return;
    }
    (async () => {
      const _subscriptionDetails = subscriptionDetails?.rawData;
      if (!_subscriptionDetails) {
        setIsLoading(false);
        return;
      }

      const subscriptionProducts = _subscriptionDetails.items?.data?.map(
        (item: Stripe.SubscriptionItem) => item.price.product
      );

      // Find plan product: must be in subscription AND identified as a base plan
      // (by env var product ID or by product metadata.product_id)
      let _planProduct = products.find(
        (product) =>
          subscriptionProducts?.includes(product.id) &&
          isBasePlanProduct(product)
      );

      // Find addon product: prefer one that's in the subscription,
      // fall back to the one linked to the user's base plan via base_product metadata.
      // When multiple addon products match the same base plan, prefer the one
      // whose prices have a base_price matching the subscription's plan price.
      let _addonProduct = products.find(
        (product) =>
          subscriptionProducts?.includes(product.id) && isAddonProduct(product)
      );
      if (!_addonProduct && _planProduct) {
        const subPlanPriceId = _subscriptionDetails?.items?.data?.find(
          (item: Stripe.SubscriptionItem) => {
            const pid = item.price.product as string;
            return BASE_PRODUCTS.includes(pid) || pid === _planProduct?.id;
          }
        )?.price?.id;
        const matchingAddons = products.filter(
          (product) =>
            isAddonProduct(product) &&
            product.metadata?.base_product === _planProduct?.id
        );
        // Prefer the addon whose prices have a base_price matching the plan price
        _addonProduct =
          (subPlanPriceId &&
            matchingAddons.find((product) =>
              product.prices?.some(
                (price) => price.metadata?.base_price === subPlanPriceId
              )
            )) ||
          matchingAddons[0];
      }

      // If plan not found by ID/metadata, infer by elimination:
      // the subscription product that is NOT the addon
      if (!_planProduct && _addonProduct) {
        const addonId = _addonProduct.id;
        _planProduct = products.find(
          (product) =>
            subscriptionProducts?.includes(product.id) && product.id !== addonId
        );
      }
      // If addon not found, infer as the subscription product that isn't the plan
      if (!_addonProduct && _planProduct) {
        const planId = _planProduct.id;
        _addonProduct = products.find(
          (product) =>
            subscriptionProducts?.includes(product.id) && product.id !== planId
        );
      }

      if (!_planProduct || !_addonProduct) {
        console.error('Failed to match products', {
          subscriptionProducts,
          baseProducts: BASE_PRODUCTS,
          productMetadata: products.map((p) => ({
            id: p.id,
            product_category: p.metadata?.product_category,
          })),
          foundPlan: !!_planProduct,
          foundAddon: !!_addonProduct,
        });
        setErrorElement(<ErrorConfigInvalid />);
        setIsLoading(false);
        return;
      }

      setPlanProduct(_planProduct as Product);
      setPlanPrices(_planProduct.prices as PriceWithRecurring[]);
      setAddonProduct(_addonProduct as Product);
      setAddonPrices(_addonProduct.prices as PriceWithRecurring[]);

      // Use the subscription's actual plan price as the selected price.
      // This may be an archived legacy price — that's correct, because
      // addon base_price metadata references the actual subscription price.
      // Fall back to an active catalog price matching the billing interval.
      const subPlanItem = _subscriptionDetails?.items?.data?.find(
        (item: Stripe.SubscriptionItem) => {
          const productId = item.price.product as string;
          return (
            BASE_PRODUCTS.includes(productId) || productId === _planProduct?.id
          );
        }
      );
      const subPlanPriceId = subPlanItem?.price?.id;
      const recurringInterval =
        subPlanItem?.price?.recurring?.interval || 'month';
      const catalogPriceForInterval = _planProduct?.prices.find(
        (price) => price.recurring?.interval === recurringInterval
      );
      setSelectedPlanPriceId(
        subPlanPriceId ||
          catalogPriceForInterval?.id ||
          _planProduct.prices[0].id
      );

      setCurrentSubscription(_subscriptionDetails);

      const _formerAddonPendingQuantity = getPendingQuantity(
        _subscriptionDetails,
        _addonProduct.id
      );
      const _formerAddonRealQuantity = getRealQuantity(
        _subscriptionDetails,
        _addonProduct.id
      );
      setFormerAddonPendingQuantity(_formerAddonPendingQuantity);
      setFormerAddonRealQuantity(_formerAddonRealQuantity);
      setNewAddonQuantity(_formerAddonPendingQuantity);
      setIsLoading(false);
    })();
  }, [customerId, products, subscriptionDetails]);

  useEffect(() => {
    if (!customerId) {
      return;
    }

    const calculateInvoice = async () => {
      if (!addonProduct) {
        return;
      }

      if (selectedPlanPriceId && (hasChangedCredits || hasChangedPlan)) {
        try {
          setIsLoadingInvoice(true);

          const invoiceCalculations = await Promise.all([
            caculatelInvoicePreviewToday({
              customerId,
              subscription: currentSubscription,
              planPrices,
              addonPrices,
              selectedPlanPriceId,
              formerAddonQuantity: formerAddonRealQuantity,
              newAddonQuantity,
            }),
            calculateInvoicePreviewForNextTime({
              customerId,
              subscription: currentSubscription,
              planPrices,
              addonPrices,
              selectedPlanPriceId,
              newAddonQuantity,
            }),
          ]);
          const {
            invoice: _invoiceToday,
            invoiceLines: _requestedInvoiceLines,
          } = invoiceCalculations[0];
          const _finalNextTime = invoiceCalculations[1];
          setFinalInvoiceNextTime(_finalNextTime);
          setFinalInvoiceToday(_invoiceToday);
          setRequestedInvoiceLines(_requestedInvoiceLines);
        } catch (e) {
          const stripeMessage =
            e instanceof AxiosError ? e?.response?.data?.message : '';
          if (stripeMessage?.includes('has not provided a payment method')) {
            Notify.error('Please add a payment method first');
            setErrorElement(<ErrorPaymentMethodMissing />);
          } else {
            console.error('Invoice preview failed:', stripeMessage || e);
            Notify.error('Failed to calculate preview invoice');
            setErrorElement(<ErrorConfigInvalid />);
          }
        }
        setIsLoadingInvoice(false);
      }
    };

    const timer = setTimeout(() => {
      calculateInvoice();
    }, 500);
    return () => clearTimeout(timer);
  }, [
    customerId,
    addonProduct,
    selectedPlanPriceId,
    currentSubscription,
    formerAddonPendingQuantity,
    formerAddonRealQuantity,
    newAddonQuantity,
    remainingCredits,
  ]);

  if (isLoading) {
    return (
      <div className="preview-invoice-container">
        <h2 className="preview-invoice--header">Loading...</h2>
      </div>
    );
  }

  if (errorElement && !planProduct) {
    return (
      <div className="preview-invoice-container">
        <div className="error-container">{errorElement}</div>
      </div>
    );
  }

  // Resolve the addon price to use. When the subscription already has an addon
  // item, use that price (it's already correct). Only fall back to catalog
  // matching for first-time credit purchases (no addon in subscription).
  const currentAddonSubItem = currentSubscription?.items?.data?.find(
    (item: Stripe.SubscriptionItem) => item.price.product === addonProduct?.id
  );
  const selectedPlanInterval =
    currentSubscription?.items?.data?.find((item: Stripe.SubscriptionItem) => {
      const pid = item.price.product as string;
      return BASE_PRODUCTS.includes(pid) || pid === planProduct?.id;
    })?.price?.recurring?.interval || 'month';
  const matchingAddonPrice = currentAddonSubItem
    ? addonPrices.find((p) => p.id === currentAddonSubItem.price.id) ||
      _getMatchingAddonPrice({
        addonPrices,
        selectedPlanPriceId,
        selectedPlanInterval,
      })
    : _getMatchingAddonPrice({
        addonPrices,
        selectedPlanPriceId,
        selectedPlanInterval,
      });
  const creditsPerPack =
    getCreditsMonthly(matchingAddonPrice, null) ??
    getCreditsMonthly(currentAddonSubItem?.price, addonProduct, 200) ??
    200;

  // Plan items are always quantity 1 — the subscription item price accurately
  // reflects the user's current plan, so check it first.
  const currentPlanSubItem = currentSubscription?.items?.data?.find(
    (item: Stripe.SubscriptionItem) => item.price.product === planProduct?.id
  );
  const matchingPlanPrice = planPrices.find(
    (p) => p.id === selectedPlanPriceId
  );
  const planBaseCredits =
    getCreditsMonthly(currentPlanSubItem?.price, null) ??
    getCreditsMonthly(matchingPlanPrice, planProduct, 200) ??
    200;

  const additionalCredits = formerAddonPendingQuantity * creditsPerPack;
  const totalCredits = planBaseCredits + additionalCredits;

  return (
    <div className="preview-invoice-container">
      {/* Current Subscription */}
      {currentSubscription && (
        <div className="current-subscription-section">
          <h3 className="current-subscription-header">Current Subscription</h3>
          <div className="current-subscription-details">
            <div className="subscription-item">
              <span className="label">Plan:</span>
              <span className="value">
                {planProduct?.name}
                {currentSubscription.status === 'trialing' && ' (trial)'}
              </span>
            </div>
            <div className="subscription-item">
              <span className="label">Credit upgrades:</span>
              <span className="value">
                {additionalCredits} ({formerAddonPendingQuantity} pack(s) of{' '}
                {creditsPerPack})
              </span>
            </div>
            {oneTimeCreditsPurchased > 0 && (
              <div className="subscription-item">
                <span className="label">One-time credits:</span>
                <span className="value">
                  {oneTimeCreditsRemaining}/{oneTimeCreditsPurchased} left
                </span>
              </div>
            )}
            <div className="subscription-item">
              <span className="label">Total credits available:</span>
              <span className="value">
                {totalCredits + oneTimeCreditsRemaining}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Input */}
      <div className="quantity-section">
        <label htmlFor="addon-quantity" className="quantity-label">
          {formerAddonPendingQuantity === 0
            ? 'Purchase additional credit upgrades:'
            : 'Adjust credit upgrades to '}
        </label>
        <div className="quantity-input-wrapper">
          <InputNumber
            id="addon-quantity"
            className="quantity-input"
            type="number"
            min={0}
            step={1}
            value={newAddonQuantity}
            onChange={(newQuantity) => setNewAddonQuantity(newQuantity || 0)}
            disabled={isProcessing}
          />
          <span className="quantity-suffix">packs</span>
        </div>
      </div>

      {/* Explaination for trial subscription */}
      {currentSubscription?.status === 'trialing' &&
        newAddonQuantity - formerAddonPendingQuantity > 0 && (
          <div>
            <p style={{ color: 'orange' }}>
              You are currently in a trial period. Your trial will convert to a
              paid subscription immediately when you buy more credits.
            </p>
          </div>
        )}

      {/* Order Details - Only show when quantity is modified */}
      {newAddonQuantity !== formerAddonPendingQuantity && (
        <Spin spinning={isLoadingInvoice}>
          <div className="order-details">
            <h3 className="order-header">Order details</h3>

            {/* Line Items */}
            {finalInvoiceToday?.lines?.data &&
              finalInvoiceToday.lines.data.map((line) => {
                const lineProductId =
                  line.pricing?.price_details?.product || 'unknown-product';
                const lineProduct = [planProduct, addonProduct].find(
                  (_product) => _product?.id === lineProductId
                );
                return (
                  <div key={line.id} className="line-item">
                    <div className="line-item-details">
                      <div className="line-item-name">{lineProduct?.name}</div>
                      <div className="line-item-type">{line.description}</div>
                    </div>
                    <div className="line-item-amount">
                      {formatCurrency(line.amount, line.currency)}
                    </div>
                  </div>
                );
              })}

            {/* Total */}
            <div className="total first">
              <div>Total due today</div>
              {finalInvoiceToday && (
                <div>
                  {finalInvoiceToday?.next_payment_attempt
                    ? formatCurrency(0, finalInvoiceToday.currency)
                    : formatCurrency(
                        finalInvoiceToday.total,
                        finalInvoiceToday.currency
                      )}
                </div>
              )}
            </div>
            <div className="total">
              <div>Total due on next renewal</div>
              {finalInvoiceNextTime && (
                <div>
                  {formatCurrency(
                    finalInvoiceNextTime.total,
                    finalInvoiceNextTime.currency
                  )}
                </div>
              )}
            </div>
          </div>

          {increasingCredits && renewalInfo && (
            <div className="renewal-info">
              <div className="renewal-icon">ⓘ</div>
              <div className="renewal-text">
                Your subscription will auto renew on {renewalInfo.date}. You
                will be charged {renewalInfo.amount} + tax.
              </div>
            </div>
          )}
        </Spin>
      )}

      {/* Error */}
      <div className="error-container">{errorElement}</div>

      {/* Explanation for grace period OR consent confirmation */}
      {newAddonQuantity !== formerAddonPendingQuantity && (
        <>
          {newAddonQuantity <= formerAddonRealQuantity && (
            <div className="downgrade-notice">
              Your existing credit allowance will remain valid until your next
              renewal date of{' '}
              {currentSubscription?.items?.data?.[0]?.current_period_end
                ? new Date(
                    currentSubscription.items.data[0].current_period_end * 1000
                  ).toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'the end of your billing period'}
              .
            </div>
          )}
          {!decreasingCredits && (
            <div className="consent-checkbox">
              <Checkbox
                checked={isConfirmedConsent}
                onChange={(e) => setIsConfirmedConsent(e.target.checked)}
                disabled={isProcessing}
              >
                You agree that Cirra will charge your card in the amount above
                now and on a recurring monthly basis until you cancel in
                accordance with our{' '}
                <a
                  href="https://cirra.ai/docs/tos"
                  target="_blank"
                  rel="noreferrer"
                >
                  terms
                </a>
                . You can cancel at any time.
              </Checkbox>
            </div>
          )}
        </>
      )}

      {/* Action buttons */}
      <div className="action-buttons">
        <Button
          className="cancel-button"
          onClick={() => {
            // If this tab was opened by another tab (CreditButton), close it
            // Otherwise, navigate back to the main screen
            if (window.opener) {
              window.close();
            } else {
              navigate('/');
            }
          }}
          disabled={isProcessing}
        >
          Cancel
        </Button>

        {newAddonQuantity !== formerAddonPendingQuantity && (
          <Button
            className="subscribe-button"
            disabled={
              isProcessing ||
              isLoadingInvoice ||
              !!errorElement ||
              (!hasChangedCredits && !hasChangedPlan) ||
              (!decreasingCredits && !isConfirmedConsent)
            }
            type="primary"
            color="primary"
            loading={isProcessing}
            onClick={async () => {
              if (!customerId || !finalInvoiceToday) {
                return;
              }
              setIsProcessing(true);
              try {
                await upsertSubscription({
                  requestedInvoiceLines,
                });
                Notify.success('Updated subscription successfully!');
                await sleep(2000);

                // If this tab was opened by another tab (CreditButton), close it
                // Otherwise, navigate back to the main screen and refresh credit balance
                if (window.opener) {
                  window.close();
                } else {
                  // Only refresh credit balance when navigating back (not closing)
                  await dispatch(fetchCreditBalance());
                  navigate('/');
                }
              } catch (error: unknown) {
                setIsProcessing(false);
                const stripeMessage =
                  error instanceof AxiosError
                    ? error?.response?.data?.message
                    : '';
                if (
                  stripeMessage?.includes(
                    'no attached payment source or default payment method'
                  )
                ) {
                  Notify.error('Please add a payment method first');
                  setErrorElement(<ErrorPaymentMethodMissing />);
                } else {
                  console.error(
                    'Failed to update subscription:',
                    stripeMessage || error
                  );
                  Notify.error('Failed to update subscription');
                  setErrorElement(<ErrorConfigInvalid />);
                }
              }
            }}
          >
            {decreasingCredits ? 'Downgrade' : 'Update Subscription'}
          </Button>
        )}
      </div>
      <div className="contact-sales">
        Any questions? Contact&nbsp;
        <Link to="mailto:sales@cirra.ai">sales@cirra.ai</Link>
      </div>
    </div>
  );
};

export default PreviewInvoice;
